import { useCallback, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useQueryClient } from '@tanstack/react-query';
import type { F } from 'ts-toolbelt';

import { invariant } from '@e-socna/utils';
import { getDocumentName } from '@e-socna/data-layer';
import type { Id } from '@e-socna/data-layer';

import { userFriendlyErrors } from '../../../utils/back-errors.util';
import { MissionPage_OneMission_QueryDocument } from '../../../pages/Mission/hooks/useFindOneMissionQuery.hook';
import { MissionReportPage_OneReport_QueryDocument } from '../../../pages/MissionReport/hooks/useFindOneReportQuery.hook';
import type { BackError } from '../../../utils/back-errors.util';

import { memoForm2ApiMapper } from '../../MemoBeforeSubmit/mappers/memo.form2api.mapper';
import { documentChoiceForm2ApiMapper } from '../../DocumentTemplates/mappers/document-choice.form2api.mapper';
import type { FolderBusinessEntityStateFormValues } from '../../FolderBusinessEntityState/types/values.type';
import type { UseUpdateFolderBusinessEntityStateSubmit } from '../../FolderBusinessEntityState/types/use-submit.type';

import { useUpdateMissionReportInfoStateMutation } from './useUpdateMissionReportInfoStateMutation.hook';
import type { MissionReportStateForm_UpdateReportInfoState_Result } from './useUpdateMissionReportInfoStateMutation.hook';

export const useUpdateMissionReportInfoStateSubmitFactory = ({
  missionId,
  reportId,
  reportInfoId,
  onSuccess: handleSuccessFactoryLevel,
}: Readonly<{
  missionId: Id;
  reportId: Id;
  reportInfoId: Id;
  onSuccess?: (params: Readonly<{ actionId: Id; newStateId: Id }>) => void;
}>): UseUpdateFolderBusinessEntityStateSubmit =>
  function useUpdateMissionReportInfoStateSubmit({
    memos,
    onSuccess: handleSuccessSubmitLevel,
  }: F.Parameters<UseUpdateFolderBusinessEntityStateSubmit>[0]) {
    const [formError, setFormError] = useState<readonly string[] | null>(null);

    const { enqueueSnackbar } = useSnackbar();
    const queryClient = useQueryClient();

    const { mutate: updateMissionReportInfoStateMutation } =
      useUpdateMissionReportInfoStateMutation();

    const handleSubmit = useCallback(
      (
        values: Readonly<FolderBusinessEntityStateFormValues>,
        {
          setSubmitting,
        }: Readonly<{ setSubmitting: (isSubmitting: boolean) => void }>,
      ) => {
        setFormError(null);

        const onSuccess = (
          result: Readonly<MissionReportStateForm_UpdateReportInfoState_Result>,
        ) => {
          enqueueSnackbar('Le statut de votre rapport a bien été mis à jour.');

          void queryClient.invalidateQueries({
            queryKey: [
              getDocumentName(MissionPage_OneMission_QueryDocument),
              {
                id: missionId,
              },
            ],
          });

          void queryClient.invalidateQueries({
            queryKey: [
              getDocumentName(MissionReportPage_OneReport_QueryDocument),
              {
                id: reportId,
              },
            ],
          });

          const updatedReportInfo =
            result.updateReportInfoState.reportInfos.find(
              (reportInfo) => reportInfo._id === reportInfoId,
            );

          invariant(!!updatedReportInfo, 'Report info not found');

          handleSuccessFactoryLevel?.({
            actionId: values.actionId,
            newStateId: updatedReportInfo.stateId,
          });

          handleSuccessSubmitLevel?.();

          setSubmitting(false);
        };

        const onError = (error: unknown) => {
          setFormError(userFriendlyErrors(error as BackError));

          setSubmitting(false);
        };

        updateMissionReportInfoStateMutation(
          {
            missionId,
            reportInfoId,
            ...documentChoiceForm2ApiMapper(values),
            versionData: memoForm2ApiMapper(memos),
          },
          {
            onSuccess,
            onError,
          },
        );
      },
      [
        enqueueSnackbar,
        handleSuccessSubmitLevel,
        memos,
        queryClient,
        updateMissionReportInfoStateMutation,
      ],
    );

    return {
      formError,
      handleSubmit,
    };
  };
